import { useEffect, useState } from 'react';

import { FileStatus } from 'services/file.service';
import { UserRole } from 'services/share.service';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import {
  fetchPaymentPricing,
  fetchExtensions,
  fetchFinalcuts,
  setStoryAudio,
  fetchProjects,
  fetchStories,
  fetchUser,
  fetchFonts,
  selectStory,
  fetchFolders,
  selectProject,
  fetchStorageSize,
} from 'components/app/_mediator/app-data.slice';
import { setRecentAction } from 'components/app/_mediator/app-logger.slice';
import { fetchFiles, selectFiles } from 'components/app/_mediator/app-files.slice';
import { setProjectLoaded, setStoryLoaded } from 'components/app/_mediator/app.slice';
import {
  setSelectedFinalcut,
  setSelectedProject,
  setSelectedStory,
} from 'components/app/projects/_mediator/projects.slice';
import { setAudioId } from 'components/app/audio-track/_mediator/audio-track.slice';
import {
  fetchMarkers,
  handleClips,
  fetchClips,
  fetchStoryboard,
  cleanDeletedClips,
  fetchPresets,
  fetchStoryConfig,
  setSelectedMarkerPreset,
} from 'components/app/editor/_mediator/editor.slice';
import { getDefaultFc, getDefaultProject, getDefaultStory, isNull } from 'components/app/_helpers/_app.helper';
import { sbApi } from 'components/app/editor/_mediator/editor-clips/editor-clips-sb.helper';
import { fetchFinalCut } from 'components/app/finalcuts/_mediator/fc-data.slice';
import { useFinalcutCb } from 'components/app/finalcuts/_mediator/use-finalcut-cb';

export const useAppFetcher = () => {
  const dispatch = useAppDispatch();
  useFetchFinalcut();
  useFetchStory();
  useEffect(() => {
    dispatch(fetchUser());
    dispatch(fetchFolders());
    dispatch(fetchStorageSize());
    dispatch(fetchExtensions());
    dispatch(fetchFonts());
    dispatch(fetchPaymentPricing());
  }, []);
};

/* H O O K S */

const useFetchStory = () => {
  const storyData = useAppSelector(selectStory);
  const projectData = useAppSelector(selectProject);
  const audios = useAppSelector(selectFiles).audios;
  const audioId = useAppSelector((state) => state.audio.audioId);
  const stories = useAppSelector((state) => state.app__data.stories);
  const fileStatuses = useAppSelector((state) => state.app__files.statuses);
  const storyConfig = useAppSelector((state) => state.editor.storyConfig);
  const { projectLoaded, storyLoaded, appRestored } = useAppSelector((state) => state.app);
  const selectedProject = useAppSelector((state) => state.projects.selectedProject);
  const selectedFolder = useAppSelector((state) => state.projects.selectedFolder);
  const selectedStory = useAppSelector((state) => state.projects.selectedStory);
  const { pending, buffer } = useAppSelector((state) => state.monitorReq);
  const dispatch = useAppDispatch();

  const [isStoryPending, setIsStoryPending] = useState(true);
  const [isProjectPending, setIsProjectPending] = useState(true);

  const [storyInit, setStoryInit] = useState(false);
  const [audioInit, setAudioInit] = useState(false);

  const resetState = () => {
    setStoryInit(false);
    setAudioInit(false);
  };

  const checkStoryIsPending = (storyId: StoryId) => {
    const payloads = [...(pending ?? []), ...(buffer ?? [])];
    const pendingStory = payloads.find((payload) => payload.storyId === storyId);
    return !!pendingStory;
  };

  const checkProjectIsPending = (projectId: number) => {
    const payloads = [...(pending ?? []), ...(buffer ?? [])];
    const pendingProject = payloads.find((payload) => payload.projectId === projectId);
    return !!pendingProject;
  };

  const handleAudioInit = async () => {
    if (storyLoaded) return; // race condition
    if (!projectLoaded || !storyData || !audioId || !storyConfig) return;
    const storyAudioId = storyData.selectedAudioFileId;
    if (!storyAudioId) await dispatch(setStoryAudio({ storyId: storyData.storyId, audioFileId: audioId }));
    const resPresets = dispatch(fetchPresets(audioId)).unwrap();
    const resMarkers = dispatch(fetchMarkers(storyData.storyId)).unwrap();
    const resSb = dispatch(fetchStoryboard(storyData.storyId)).unwrap();
    Promise.all([resPresets, resMarkers, resSb]).then(([resPresets, resMarkers, sb]) => {
      if (resMarkers.selectedMarkerPreset === null) dispatch(setSelectedMarkerPreset(resPresets[0].id));
      dispatch(handleClips({ data: { storyBoard: sbApi(sb).init(resMarkers.markers.length) }, backupSkip: true }));
      setAudioInit(true);
    });
  };

  useEffect(() => {
    if (isNull(pending) && isNull(buffer)) {
      setIsStoryPending(false);
      setIsProjectPending(false);
    }
  }, [pending, buffer]);

  /* step 0: getting projects */
  useEffect(() => {
    if (selectedFolder === null) return;
    dispatch(fetchProjects())
      .unwrap()
      .then((projects) => {
        if (appRestored) dispatch(setSelectedProject(getDefaultProject(projects)));
      });
  }, [selectedFolder]);

  /* step 1: dispatch(setProjectLoaded(true)) */
  useEffect(() => {
    if (!selectedProject) return;
    if (checkProjectIsPending(selectedProject)) {
      setIsProjectPending(true);
      return;
    }

    const filesPromise = dispatch(fetchFiles(selectedProject));
    const storiesPromise = dispatch(fetchStories(selectedProject))
      .unwrap()
      .then((stories) => {
        if (appRestored) dispatch(setSelectedStory(getDefaultStory(stories)));
      });
    Promise.all([filesPromise, storiesPromise]).then(() => dispatch(setProjectLoaded(true)));

    return () => {
      dispatch(setProjectLoaded(false));
      resetState();
    };
  }, [selectedProject, isProjectPending]);

  /* step 2-a: setStoryInit(true) */
  useEffect(() => {
    if (!projectLoaded || isNull(selectedStory)) return;
    if (checkStoryIsPending(selectedStory)) {
      setIsStoryPending(true);
      return;
    }

    if (projectData && projectData.role < UserRole.Reader) dispatch(cleanDeletedClips(selectedStory));

    const res1 = dispatch(fetchClips({ storyId: selectedStory }));
    const res2 = dispatch(fetchStoryConfig(selectedStory));
    const res3 = dispatch(fetchFinalcuts(selectedStory))
      .unwrap()
      .then((finalcuts) => {
        if (appRestored) dispatch(setSelectedFinalcut(getDefaultFc(finalcuts)));
      });

    Promise.all([res1, res2, res3]).then(() => setStoryInit(true));

    return () => resetState();
  }, [projectLoaded, selectedStory, isStoryPending]);

  /* step 2-b-1: dispatch(setAudioId(audioFileId)) */
  useEffect(() => {
    if (storyLoaded) return; // race condition
    if (audios.length === 0) return;
    if (!storyData || !stories) return;
    const storyAudio = storyData.selectedAudioFileId;
    const audioFileId = storyAudio ?? audios[0].id;
    const status = fileStatuses[audioFileId];
    if (status !== FileStatus.Available) return;
    dispatch(setAudioId(audioFileId));
  }, [storyLoaded, storyData?.storyId, audios.length, fileStatuses]);

  /* step 2-b-2: setAudioInit(true); */
  useEffect(() => {
    handleAudioInit();
  }, [storyLoaded, projectLoaded, audioId, storyData?.storyId, storyConfig]);

  /* *** */

  useEffect(() => {
    if (storyLoaded || !storyInit || !audioInit) return;
    dispatch(
      setRecentAction({
        key: 'SERV',
        desc: `fetcher: story loaded`,
        payload: { selectedProject, selectedStory, audioId },
      })
    );
    dispatch(setStoryLoaded(true));
  }, [storyInit, audioInit]);
};

const useFetchFinalcut = () => {
  const { selectedFinalcut } = useAppSelector((state) => state.projects);
  const { storyLoaded, appRestored } = useAppSelector((state) => state.app);
  const { finalcuts } = useAppSelector((state) => state.app__data);
  const { fcReset } = useFinalcutCb();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!appRestored || !storyLoaded) return;
    if (!finalcuts.length) return;
    if (isNull(selectedFinalcut)) dispatch(setSelectedFinalcut(finalcuts[0].id));
  }, [storyLoaded, appRestored]);

  useEffect(() => {
    if (isNull(selectedFinalcut)) return;
    dispatch(fetchFinalCut(selectedFinalcut));
    dispatch(
      setRecentAction({
        key: 'SERV',
        desc: `fetcher: fc loaded`,
        payload: { selectedFinalcut },
      })
    );
    fcReset();
  }, [selectedFinalcut]);
};
